import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { toast } from 'react-toastify';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/contexts/UserContext';
import axiosInstance from '@/utils/axiosInstance';
import { ArrowLeft, Check, X, Minus, Plus, Trash2, Copy, Percent, Search } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import Loader from '@/components/common/Loader';
import BottomNav from '@/components/layouts/Footer/BottomNav';

const API_URL = import.meta.env.VITE_API_URL;

export default function ProductPricing() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [changedItems, setChangedItems] = useState(new Set());
  const [navigation, setNavigation] = useState('');
  const [newCategory, setNewCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const navigate = useNavigate();
  const { user } = useUser();

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      const currCategory = selectedCategory;
      const idx = categories.findIndex((i) => i.id === currCategory);

      if (eventData?.dir === 'Right' && idx > 0) {
        if (changedItems.size > 0) {
          setNewCategory(categories[idx - 1].id);
          setShowConfirmDialog(true);
        } else {
          setSelectedCategory(categories[idx - 1].id);
        }
      } else if (eventData?.dir === 'Left' && idx < categories.length - 1) {
        if (changedItems.size > 0) {
          setNewCategory(categories[idx + 1].id);
          setShowConfirmDialog(true);
        } else {
          setSelectedCategory(categories[idx + 1].id);
        }
      }
    },
  });

  const handleNavigation = (to) => {
    if (changedItems.size > 0) {
      setShowConfirmDialog(true);
      setNavigation(to.toString());
    } else {
      navigateTo(to);
    }
  };

  const navigateTo = (to) => {
    if (typeof to === 'number') {
      navigate(to);
    } else {
      navigate(to);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axiosInstance.get(
        `${API_URL}/rest/subziwale/api/v1/products/categories`,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );
      setCategories(data.map((category) => ({ id: category.id, name: category.name })));
      setSelectedCategory(data[0]?.id);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (controller) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `${API_URL}/rest/subziwale/api/v1/products?category=${selectedCategory}`,
        {
          headers: {
            'X-VENDOR-ID': user?.id,
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
          signal: controller.signal,
        }
      );

      const processedData = data.map((product) => ({
        ...product,
        mrp: product.mrp || product.netPrice || 0,
        netPrice: product.netPrice || product.mrp || 0,
      }));

      setProducts(processedData);
      setFilteredProducts(processedData);
      setSelectedProducts(new Set());
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = (id, field, newValue) => {
    const updatedProducts = products.map((product) => {
      if (product.productId === id) {
        const updatedProduct = {
          ...product,
          [field]: newValue,
        };
        if (updatedProduct[field] !== product[field]) {
          setChangedItems((prev) => new Set(prev).add(id));
        } else {
          setChangedItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }
        return updatedProduct;
      }
      return product;
    });
    setProducts(updatedProducts);
    filterProducts(updatedProducts, searchQuery);
  };

  const toggleAvailability = (id) => {
    const updatedProducts = products.map((product) => {
      if (product.productId === id) {
        const updatedProduct = {
          ...product,
          available: !product.available,
        };
        setChangedItems((prev) => new Set(prev).add(id));
        return updatedProduct;
      }
      return product;
    });
    setProducts(updatedProducts);
    filterProducts(updatedProducts, searchQuery);
  };

  const saveChanges = async () => {
    const changedProducts = products
      .filter((product) => changedItems.has(product.productId))
      .map((product) => ({
        productId: product.productId,
        netPrice: product.netPrice,
        mrp: product.mrp,
        available: product.available,
      }));
    const vendorId = user?.id;
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const refreshToken = localStorage.getItem('refreshToken');
      await axiosInstance.put(`${API_URL}/rest/subziwale/api/v1/products/vendor`, changedProducts, {
        headers: {
          'X-Vendor-Id': vendorId,
          sessionToken,
          refreshToken,
        },
      });
      setChangedItems(new Set());
      toast.success('Prices updated successfully');
    } catch (error) {
      console.error('Error saving changes', error);
      toast.error('Error updating prices');
    }
  };

  const filterProducts = (productsToFilter, query) => {
    const filtered = productsToFilter.filter((product) =>
      product.productName.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const selectAllProducts = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map((p) => p.productId)));
    }
  };

  const isAllAvailable = useMemo(() => {
    if (products && products?.length > 0) {
      const notAvailableproduct = products?.filter((p) => p.available === false);
      console.log(notAvailableproduct);
      return notAvailableproduct?.length === 0;
    } else {
      return false;
    }
  }, [products]);

  const markAvailable = () => {
    const productsToUpdate =
      selectedProducts.size > 0
        ? products.filter((p) => selectedProducts.has(p.productId))
        : products;

    console.log(productsToUpdate);

    const updatedProducts = products.map((product) => {
      if (productsToUpdate.some((p) => p.productId === product.productId)) {
        const updatedProduct = {
          ...product,
          available: isAllAvailable ? false : true,
        };
        setChangedItems((prev) => new Set(prev).add(product.productId));
        return updatedProduct;
      }
      console.log(product);

      return product;
    });

    setProducts(updatedProducts);
    filterProducts(updatedProducts, searchQuery);
    toast.info(`Updated ${productsToUpdate.length} products`);
  };

  const applyDiscount = (percentage) => {
    const productsToUpdate =
      selectedProducts.size > 0
        ? products.filter((p) => selectedProducts.has(p.productId))
        : products;

    const discountFactor = 1 - percentage / 100;

    const updatedProducts = products.map((product) => {
      if (productsToUpdate.some((p) => p.productId === product.productId)) {
        const updatedProduct = {
          ...product,
          netPrice: Math.round(product.mrp * discountFactor),
        };
        setChangedItems((prev) => new Set(prev).add(product.productId));
        return updatedProduct;
      }
      return product;
    });

    setProducts(updatedProducts);
    filterProducts(updatedProducts, searchQuery);
    toast.info(`Applied ${percentage}% discount to ${productsToUpdate.length} products`);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    if (user && selectedCategory) {
      fetchProducts(controller);
    }
    return () => controller.abort();
  }, [selectedCategory, user]);

  useEffect(() => {
    filterProducts(products, searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setShowBulkActions(selectedProducts.size > 0);
  }, [selectedProducts]);

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="w-full sticky top-0">
        <header className="bg-white border-b px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigation(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold">Pricing & Availability</h1>
            <Link to="/vendor/profile" className="ml-auto">
              <Avatar className="h-9 w-9 border border-gray-200">
                <AvatarImage src="https://github.com" alt={user?.name} />
                <AvatarFallback className="bg-blue-100 text-blue-600 font-medium">
                  {user?.name?.slice(0, 1)}
                </AvatarFallback>
              </Avatar>
            </Link>
          </div>
        </header>

        {/* Categories */}
        <div className=" shadow-sm">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex items-center px-4 py-2 gap-2 min-w-full">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    if (changedItems.size > 0) {
                      setNewCategory(category.id);
                      setShowConfirmDialog(true);
                    } else {
                      setSelectedCategory(category.id);
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                    category.id === selectedCategory
                      ? 'bg-[#39c55e] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className=" px-4 py-1 z-20 border-t border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Changes Indicator */}
        {changedItems.size > 0 && (
          <div className="bg-yellow-50 border-b px-4 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-yellow-800">
                {changedItems.size} product{changedItems.size > 1 ? 's' : ''} modified
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    fetchProducts({ signal: new AbortController().signal });
                    setChangedItems(new Set());
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors flex items-center gap-1"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Discard
                </button>
                <button
                  onClick={saveChanges}
                  className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                >
                  Save All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="bg-white border-b px-2 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={
                  selectedProducts.size > 0 && selectedProducts.size === filteredProducts.length
                }
                onCheckedChange={selectAllProducts}
                className="data-[state=checked]:bg-[#39c55e] data-[state=checked]:text-white"
              />
              <label htmlFor="select-all" className="text-xs font-medium cursor-pointer">
                {selectedProducts.size > 0 ? `${selectedProducts.size} selected` : 'Select All'}
              </label>
            </div>
            <div className={`flex gap-2 ${isAllAvailable ? '' : 'mr-4'}`}>
              <button
                onClick={() => {
                  const discount = prompt('Enter discount percentage (e.g., 10 for 10% off MRP):');
                  if (discount && !isNaN(Number(discount))) {
                    applyDiscount(Number(discount));
                  }
                }}
                disabled={!showBulkActions}
                className="px-2 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                <Percent className="h-3.5 w-3.5" /> Discount
              </button>
              <button
                onClick={() => markAvailable()}
                disabled={!showBulkActions}
                className={`px-2 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center gap-1`}
              >
                {isAllAvailable ? 'Mark Unavailable' : 'Mark Available'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product List */}
      <div className="flex-1 overflow-auto pb-20" {...handlers}>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div className="px-2 py-2 space-y-2">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No products found matching your search.
              </div>
            ) : (
              filteredProducts.map((product) => (
                <div
                  key={product.productId}
                  className={`bg-white rounded-md border ${
                    changedItems.has(product.productId)
                      ? 'border-yellow-300 bg-yellow-50'
                      : selectedProducts.has(product.productId)
                        ? 'border-blue-200  bg-blue-200'
                        : 'border-gray-200'
                  }`}
                >
                  {/* Product Info Row */}
                  <div className="px-2 py-1 flex items-start gap-3">
                    <Checkbox
                      checked={selectedProducts.has(product.productId)}
                      onCheckedChange={() => toggleProductSelection(product.productId)}
                      className="mt-1 data-[state=checked]:bg-[#39c55e] data-[state=checked]:text-white"
                    />
                    <img
                      src={product.productImageUrl || '/placeholder.svg'}
                      alt={product.productName}
                      className="h-14 w-14 rounded-md border object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900 max-w-60 line-clamp-2">
                        {product.productName}
                      </h3>
                      <p className="text-sm text-gray-500 ">Unit: {product?.baseUnit}</p>
                    </div>
                    <div className="flex flex-col gap-1 items-center text-black text-base">
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleAvailability(product?.productId)}
                          className={`px-3 py-1 rounded-md ${
                            product?.available
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => toggleAvailability(product?.productId)}
                          className={`px-3 py-1 rounded-md ${
                            product?.available
                              ? 'bg-gray-100 text-black'
                              : 'bg-red-100 text-gray-800'
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Price Controls Row */}
                  <div className="px-2 w-full pb-3 grid grid-cols-2 gap-2">
                    {/* MRP Controls */}
                    <div className="w-1/2 flex items-center gap-3">
                      <label className="text-sm text-gray-800 mb-1 block">MRP</label>
                      <div className="flex items-center">
                        <button
                          onClick={() =>
                            updatePrice(product.productId, 'mrp', Math.max(0, product.mrp - 5))
                          }
                          className="h-8 px-1.5 flex items-center justify-center bg-green-100 rounded-l-lg border border-green-200 hover:bg-green-200 transition-colors"
                        >
                          <Minus className="h-4 w-4 text-gray-600" />
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={product.mrp || ''}
                          onChange={(e) =>
                            updatePrice(product.productId, 'mrp', Number(e.target.value))
                          }
                          className="h-8 w-14 flex-1 px-1 text-center border-y border-green-200 focus:outline-none focus:ring-inset focus:ring-1 focus:ring-green-500"
                        />
                        <button
                          onClick={() => updatePrice(product.productId, 'mrp', product.mrp + 5)}
                          className="h-8 px-1.5 flex items-center justify-center bg-green-100 rounded-r-lg border border-green-200 hover:bg-green-200 transition-colors"
                        >
                          <Plus className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Price Controls */}
                    <div className="w-1/2 items-center flex gap-2">
                      <label className="text-sm text-gray-800 mb-1 inline-block">Price</label>
                      <div className="flex">
                        <button
                          onClick={() =>
                            updatePrice(
                              product.productId,
                              'netPrice',
                              Math.max(0, product.netPrice - 5)
                            )
                          }
                          className="h-8 px-1.5 flex items-center justify-center bg-green-100 rounded-l-lg border border-green-200 hover:bg-green-200 transition-colors"
                        >
                          <Minus className="h-4 w-4 text-gray-600" />
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={product.netPrice || ''}
                          onChange={(e) =>
                            updatePrice(product.productId, 'netPrice', Number(e.target.value))
                          }
                          className="h-8 w-14 flex-1 px-1 text-center border-y border-green-200 focus:outline-none focus:ring-inset focus:ring-1 focus:ring-green-500"
                        />
                        <button
                          onClick={() =>
                            updatePrice(product.productId, 'netPrice', product.netPrice + 5)
                          }
                          className="h-8 px-1.5 flex items-center justify-center bg-green-100 rounded-r-lg border border-green-200 hover:bg-green-200 transition-colors"
                        >
                          <Plus className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            You have unsaved changes. Do you want to save before{' '}
            {newCategory && typeof newCategory === 'string' ? 'changing categories' : 'leaving'}?
          </p>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                fetchProducts({ signal: new AbortController().signal });
                setChangedItems(new Set());
                if (navigation) {
                  navigate(navigation);
                } else if (newCategory) {
                  if (typeof newCategory === 'string') {
                    setSelectedCategory(newCategory);
                  } else {
                    navigateTo(newCategory);
                  }
                  setNewCategory(null);
                }
              }}
              className="w-full sm:w-auto"
            >
              Discard changes
            </Button>
            <Button
              onClick={() => {
                saveChanges();
                setShowConfirmDialog(false);
                if (navigation) {
                  navigate(navigation);
                } else if (newCategory) {
                  if (typeof newCategory === 'string') {
                    setSelectedCategory(newCategory);
                  } else {
                    navigateTo(newCategory);
                  }
                  setNewCategory(null);
                }
              }}
              className="bg-green-600 hover:bg-green-600/90 w-full sm:w-auto"
            >
              Save and continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav page="pricing" />
    </div>
  );
}
